// Reads and writes /api. A failed call throws an ApiError carrying the server's error code, so
// pages can tell "not found" from "try again".
import type { PostPage, PostSummary, SignatureInput, SignatureResult, VersionDetail, WorkflowList, WorkflowPage } from "./types";

export class ApiError extends Error {
  readonly status: number;
  readonly code: string;
  readonly field?: string;

  constructor(status: number, code: string, field?: string) {
    super(`${status} ${code}`);
    this.status = status;
    this.code = code;
    this.field = field;
  }
}

async function call<T>(path: string, init?: RequestInit): Promise<T> {
  const response = await fetch(path, { headers: { accept: "application/json", ...(init?.body ? { "content-type": "application/json" } : {}) }, ...init });
  const body = (await response.json().catch(() => ({}))) as Record<string, unknown>;
  if (!response.ok) throw new ApiError(response.status, String(body.error ?? "internal"), body.field as string | undefined);
  return body as T;
}

const enc = encodeURIComponent;

export const api = {
  posts: () => call<{ posts: PostSummary[] }>("/api/posts").then((r) => r.posts),
  post: (slug: string) => call<PostPage>(`/api/posts/${enc(slug)}`),
  workflows: (category?: string, q?: string) => {
    const params = new URLSearchParams();
    if (category) params.set("category", category);
    if (q) params.set("q", q);
    const query = params.toString();
    return call<WorkflowList>(`/api/workflows${query ? `?${query}` : ""}`);
  },
  workflow: (slug: string) => call<WorkflowPage>(`/api/workflows/${enc(slug)}`),
  version: (slug: string, version: number) => call<VersionDetail>(`/api/workflows/${enc(slug)}/versions/${version}`),
  downloadUrl: (slug: string, version: number) => `/api/workflows/${enc(slug)}/versions/${version}/download`,
  charter: () => call<{ signatures: number }>("/api/charter"),
  sign: (input: SignatureInput) => call<SignatureResult>("/api/charter/signatures", { method: "POST", body: JSON.stringify(input) }),
};
